import { useEffect } from "react";
import { faTag } from "@fortawesome/pro-thin-svg-icons";

import Icon from "common/components/Icon";
import { WithStringValues } from "common/utils/types";
import { useListSearchState } from "common/hooks";

import Page from "client/common/components/layouts/Page";
import Body from "client/common/components/layouts/Body";
import Container from "client/common/components/layouts/Container";
import Results from "client/common/components/results/Results";
import { ProductListSource } from "client/analytics/types";
import { Paths } from "client/routes";

import { useProductDealsSearchQuery } from "../hooks";
import { DEFAULT_PAGE_SIZE } from "../constants/filterDefinitions";
import { EMPTY_PRODUCT_SELECTION, getProductFiltersFromSearch } from "../utils";
import { ProductDealsSearch } from "../types";
import ProductCardResults from "./ProductCardResults";

import styles from "../styles/DealsPage.module.less";

const initialSearch = {
  ...EMPTY_PRODUCT_SELECTION,
  page: 1,
  page_size: DEFAULT_PAGE_SIZE,
  sort: "",
};

type DealsResultsProps = {
  search: WithStringValues<ProductDealsSearch>;
  onUpdateSearch: (search: Partial<ProductDealsSearch>) => void;
};

const DealsResults = ({ search, onUpdateSearch }: DealsResultsProps) => {
  const {
    data: products,
    isLoading,
    error,
    isRefetching,
    isPlaceholderData,
  } = useProductDealsSearchQuery(search);

  if (isLoading) {
    return <Results.Loading description="Loading deals" />;
  }

  if (error || !products) {
    return <Results.FetchError error={error} entityName="deal" />;
  }

  return (
    <ProductCardResults
      products={products}
      isRefetching={isRefetching}
      isStale={isPlaceholderData}
      filters={getProductFiltersFromSearch(search)}
      onFiltersChange={onUpdateSearch}
      onPageChange={(page) => onUpdateSearch({ page })}
      listSource={ProductListSource.DealsPage}
      entityName="deal"
      empty={{ description: "Deals change often. Check back soon for new eligible savings." }}
      className={styles.container}
    />
  );
};

const DealsPage = () => {
  const [search, updateSearch] = useListSearchState<Paths["products"]["deals"]>(initialSearch);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [search.page]);

  return (
    <Page className={styles.page}>
      <div>
        <Container>
          <div className={styles.top}>
            <div className={styles.heading}>
              <h1 className={styles.title}>Deals</h1>
              <div className={styles.description}>
                Save on your pet&apos;s care with autoship and manufacturer discounts, plus
                limited-time offers, all in one place.
              </div>
            </div>
            <Icon className={styles.icon} icon={faTag} />
          </div>
        </Container>
        <div className={styles.main}>
          <Body>
            <DealsResults search={search} onUpdateSearch={updateSearch} />
          </Body>
        </div>
      </div>
    </Page>
  );
};

export default DealsPage;
